import { useState, useEffect } from "react";
import API from "../api/client";
import TopBar from "../components/layout/TopBar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Plus, Pencil, Trash2, Users, X } from "lucide-react";
import { toast } from "sonner";

export default function GroupsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [form, setForm] = useState({ name: "", member_ids: [] });

  const load = () => {
    API.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    API.get("/users").then(r => setAllUsers(r.data.filter(u => u.status === "active"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditGroup(null); setForm({ name: "", member_ids: [] }); setShowDialog(true); };
  const openEdit = (g) => { setEditGroup(g); setForm({ name: g.name, member_ids: g.members?.map(m => m.id) || [] }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      if (editGroup) {
        await API.put(`/groups/${editGroup.id}`, form);
        toast.success("Group updated");
      } else {
        await API.post("/groups", form);
        toast.success("Group created");
      }
      setShowDialog(false);
      load();
    } catch { toast.error("Failed to save group"); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/groups/${id}`);
      toast.success("Group deleted");
      load();
    } catch { toast.error("Failed to delete group"); }
  };

  const toggleMember = (userId) => {
    setForm(f => ({
      ...f,
      member_ids: f.member_ids.includes(userId)
        ? f.member_ids.filter(id => id !== userId)
        : [...f.member_ids, userId]
    }));
  };

  return (
    <>
      <TopBar title="Groups" />
      <div className="flex-1 overflow-y-auto p-6" data-testid="groups-page">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#686B74]">{groups.length} group{groups.length !== 1 ? "s" : ""}</p>
          {isAdmin && (
            <Button data-testid="create-group-btn" onClick={openCreate} size="sm" className="bg-[#111216] hover:bg-[#2B2D31] text-white">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Group
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map(g => (
            <div key={g.id} data-testid={`group-card-${g.id}`} className="bg-white border border-[#EBEBEB] rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#111216]">{g.name}</h3>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(g)} className="p-1 text-[#686B74] hover:text-[#111216] transition-colors" data-testid={`edit-group-${g.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(g.id)} className="p-1 text-[#686B74] hover:text-[#E24A4A] transition-colors" data-testid={`delete-group-${g.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-[#686B74]">
                <Users className="w-3 h-3" />
                <span>{g.member_count} member{g.member_count !== 1 ? "s" : ""}</span>
              </div>
              {g.members?.length > 0 && (
                <div className="flex -space-x-2 mt-3">
                  {g.members.slice(0, 5).map(m => (
                    <Avatar key={m.id} className="h-6 w-6 border-2 border-white">
                      <AvatarImage src={m.avatar_url} />
                      <AvatarFallback className="text-[9px] bg-[#111216] text-white">{m.name?.[0]}</AvatarFallback>
                    </Avatar>
                  ))}
                  {g.members.length > 5 && (
                    <div className="h-6 w-6 rounded-full bg-[#F4F5F8] border-2 border-white flex items-center justify-center text-[9px] text-[#686B74]">
                      +{g.members.length - 5}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {groups.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-[#686B74]">No groups yet</div>
          )}
        </div>
      </div>

      {/* Group Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{editGroup ? "Edit Group" : "Create Group"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-1 block">Group Name</label>
              <Input data-testid="group-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Engineering" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#686B74] mb-2 block">Members</label>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-[#EBEBEB] rounded-md p-2">
                {allUsers.map(u => (
                  <button
                    key={u.id}
                    data-testid={`member-toggle-${u.id}`}
                    onClick={() => toggleMember(u.id)}
                    className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm transition-colors ${
                      form.member_ids.includes(u.id) ? "bg-[#EDF0FF] text-[#4353FF]" : "hover:bg-[#F4F5F8]"
                    }`}
                  >
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={u.avatar_url} />
                      <AvatarFallback className="text-[8px] bg-[#111216] text-white">{u.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <span>{u.name}</span>
                    {form.member_ids.includes(u.id) && <X className="w-3 h-3 ml-auto" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="group-save-btn" onClick={handleSave} className="bg-[#111216] hover:bg-[#2B2D31] text-white">
              {editGroup ? "Save Changes" : "Create Group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
